/*
 * Free Shipping Progress Bar – shows how close the customer is to free shipping.
 * Uses the discounts-info custom API via useApproachingFreeShipping hook.
 * Hidden for GlobalE international customers and when there is no approaching/applied promo.
 *
 * Gradient fill matches SFRA: sunrise-yellow → sunrise-peach → sunrise-purple (left to right).
 */
import React, {useEffect, useRef} from 'react'
import PropTypes from 'prop-types'
import {Box, Text, Skeleton} from '@salesforce/retail-react-app/app/components/shared/ui'
import {Progress} from '@chakra-ui/react'
import useApproachingFreeShipping from '../../../hooks/use-approaching-free-shipping'
import useTagManager from '../../../hooks/use-tag-manager'

export const FreeShippingProgressSkeleton = ({isDesktop = false}) => (
    <Box
        display={isDesktop ? {base: 'none', lg: 'block'} : {base: 'block', lg: 'none'}}
        paddingTop={isDesktop ? 0 : '7px'}
    >
        <Skeleton height="21px" width="60%" mb="10px" />
        <Skeleton height="12px" width="100%" />
    </Box>
)

const FreeShippingProgressBar = ({isDesktop = false}) => {
    const tagManager = useTagManager()
    const {discountMsg, fillPercent, isLoading, isHidden} = useApproachingFreeShipping()

    const progressWrapperRef = useRef(null)
    const hasTracked = useRef(false)

    const variant = 'ksGradient'

    const wrapperStyles = isDesktop
        ? {
              display: {base: 'none', lg: 'block'},
              border: 'none'
          }
        : {
              display: {base: 'block', lg: 'none'},
              paddingTop: '7px'
          }
    let name = 'empty'
    useEffect(() => {
        if (isLoading || isHidden || !progressWrapperRef.current) {
            return
        }

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (!entry.isIntersecting) {
                    return
                }
                const position =
                    window.location.pathname.indexOf('/cart') !== -1 ? 'cart' : 'checkout'

                if (fillPercent >= 25 && fillPercent <= 99) {
                    name = 'mid'
                } else if (fillPercent === 100) {
                    name = 'full'
                }
                if (hasTracked.current != name) {
                    hasTracked.current = name
                    window.__dataLayerLoaded = true
                    tagManager.promoMessageImpression({
                        id: 'free_shipping_threshold',
                        name,
                        position,
                        creative: 'Free Shipping Progress Bar'
                    })
                    window.__dataLayerLoaded = false
                }

                observer.disconnect()
            },
            {
                threshold: 0.5 // Fire when 50% of the progress bar is visible
            }
        )

        observer.observe(progressWrapperRef.current)

        return () => observer.disconnect()
    }, [fillPercent, isLoading, isHidden, tagManager])

    if (isHidden && !isLoading) {
        return null
    }

    if (isLoading) {
        return <FreeShippingProgressSkeleton isDesktop={isDesktop} />
    }

    return (
        <Box ref={progressWrapperRef} className="progress-wrapper" {...wrapperStyles}>
            <Text
                fontSize="14px"
                fontFamily="body"
                color="black.100"
                fontWeight="300"
                letterSpacing="0.07px"
                dangerouslySetInnerHTML={{__html: discountMsg}}
                sx={{
                    '& b': {
                        fontWeight: '700'
                    }
                }}
                mb="10px"
            />
            <Progress value={fillPercent} variant={variant} size="discount" />
        </Box>
    )
}

FreeShippingProgressBar.propTypes = {
    isDesktop: PropTypes.bool
}

FreeShippingProgressSkeleton.propTypes = {
    isDesktop: PropTypes.bool
}

export default FreeShippingProgressBar
